import { Enemy } from '../objects/Enemy';
import { Projectile } from '../objects/Projectile';
import { BeeSwarm } from '../objects/BeeSwarm';
import { GameScene } from '../scenes/GameScene';

export class CombatManager {
  checkProjectileEnemyCollisions(projectiles: Projectile[], enemies: Enemy[]): { killed: Enemy[] } {
    const killed: Enemy[] = [];

    for (const proj of projectiles) {
      if (proj.isDestroyed) continue;

      const pBounds = proj.getBounds();

      // Skip lobbing projectiles that are still in-flight (they only take effect on landing)
      if (proj.lobbing && proj.lobTime < proj.lobDuration) continue;

      for (const enemy of enemies) {
        if (enemy.isDead) continue;
        if (enemy.enemyType === 'boulder') continue; // Boulder is invulnerable and untargetable
        if (enemy.isBurrowed) continue; // Saboteur underground — immune
        if (enemy.isGoblin) continue; // Loot Goblin — only killable by clicks

        // Skip already-hit enemies for piercing projectiles
        if (proj.piercing && proj.hitEnemyIds.has(enemy.x * 10000 + enemy.y)) continue;

        const eBounds = enemy.getBounds();

        // Circle-rect collision
        const closestX = Math.max(eBounds.x, Math.min(pBounds.x, eBounds.x + eBounds.width));
        const closestY = Math.max(eBounds.y, Math.min(pBounds.y, eBounds.y + eBounds.height));
        const dx = pBounds.x - closestX;
        const dy = pBounds.y - closestY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= pBounds.radius) {
          let damage = proj.damage;

          // Pure visual projectiles (like missed arrows) don't interact
          if (damage <= 0) continue;

          const scene = proj.scene as GameScene;
          const wasKilled = enemy.takeDamage(damage, true);

          if (damage > 0) {
            if (scene.floatingTextManager) {
              scene.floatingTextManager.spawn(enemy.x, enemy.y - 35, `${Math.floor(damage)}`, 'damage');
            }
          }
          if (wasKilled) {
            killed.push(enemy);
          }

          // Apply slow effect from sling stones
          if (proj.hasSlowEffect()) {
            enemy.applySlow(proj.slowEffect, proj.slowDuration);
          }

          // Apply poison effect from arrows
          if (proj.hasPoisonEffect()) {
            enemy.applyPoison(proj.poisonDamage, proj.poisonDuration);
          }

          // Apply fire effect from longbow arrows
          if (proj.hasFireEffect()) {
            enemy.applyFire(proj.fireDamage, proj.fireDuration);
          }

          // Apply stun effect from rocks
          if (proj.stunDuration > 0 && !wasKilled) {
            enemy.applyStun(proj.stunDuration);
          }

          // Apply knockback from spears
          if (proj.knockbackDistance > 0 && !wasKilled) {
            enemy.applyKnockback(proj.knockbackDistance);
          }

          // Firefly AoE on impact — damage nearby enemies with fire
          if (proj.projectileType === 'firefly' && proj.aoeRadius > 0) {
            for (const nearby of enemies) {
              if (nearby.isDead || nearby === enemy) continue;
              if (nearby.isBurrowed || nearby.isGoblin) continue;
              const adx = nearby.x - enemy.x;
              const ady = nearby.y - enemy.y;
              if (Math.sqrt(adx * adx + ady * ady) <= proj.aoeRadius) {
                const aoeDmg = Math.round(damage * 0.5);
                const aoeKill = nearby.takeDamage(aoeDmg, true);
                if (proj.hasFireEffect()) nearby.applyFire(proj.fireDamage, proj.fireDuration);
                if (aoeKill) killed.push(nearby);
              }
            }
            proj.destroy();
            break;
          }

          if (proj.piercing) {
            proj.hitEnemyIds.add(enemy.x * 10000 + enemy.y);
            // Check pierce limit
            if (proj.maxPierceCount > 0 && proj.hitEnemyIds.size >= proj.maxPierceCount) {
              proj.destroy();
              break;
            }
          } else if (proj.projectileType === 'crow' && !(proj as any).isReturning) {
            // Crow hits, deals damage, then returns
            (proj as any).isReturning = true;
            (proj as any).target = null; // Stop homing
            // Don't destroy
          } else {
            proj.destroy();
            break;
          }
        }
      }

      // Lobbing projectile that reached its target
      if (proj.lobbing && proj.lobTime >= (proj as any).lobDuration) {
        if (proj.projectileType === 'beehive') {
          // Spawn swarm
          const scene = proj.scene as GameScene;
          const slowEffect = (proj as any).slowEffect || 0.7;
          const swarm = new BeeSwarm(scene, proj.targetX, proj.targetY, 60, (proj as any).slowDuration || 5000, slowEffect);
          if (scene.beeSwarms) scene.beeSwarms.push(swarm);
        } else if (proj.projectileType === 'rock_heavy' && proj.aoeRadius > 0) {
          // Trebuchet AOE impact — damage all enemies in radius
          const scene = proj.scene as GameScene;
          for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.isBurrowed || enemy.isGoblin) continue;
            const dx = enemy.x - proj.targetX;
            const dy = enemy.y - proj.targetY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist <= proj.aoeRadius) {
              const damage = proj.damage;
              const wasKilled = enemy.takeDamage(damage, true);
              if (damage > 0) {
                const scene = proj.scene as GameScene;
                if (scene.floatingTextManager) {
                  scene.floatingTextManager.spawn(enemy.x, enemy.y - 35, `${Math.floor(damage)}`, 'damage');
                }
              }
              if (wasKilled) killed.push(enemy);
            }
          }
          // Ground crash animation
          if (scene.showImpactEffect) {
            scene.showImpactEffect(proj.targetX, proj.targetY, proj.aoeRadius);
          }
        }
        proj.destroy();
      }
    }

    return { killed };
  }
}
